import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiBookOpen,
  FiBriefcase,
  FiGrid,
  FiHome,
  FiLogIn,
  FiLogOut,
  FiPhoneCall,
  FiSearch,
  FiShoppingBag,
  FiUserPlus,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { resolveSettingMediaUrl, useSiteSettings } from '../context/SiteSettingsContext';
import { getLocaleFlagMeta } from '../utils/localeFlags';
import './Navbar.css';

const navigationItems = [
  { to: '/', key: 'home', icon: FiHome },
  { to: '/services', key: 'services', icon: FiBriefcase },
  { to: '/products', key: 'products', icon: FiShoppingBag },
  { to: '/articles', key: 'articles', icon: FiBookOpen },
  { to: '/about', key: 'about', icon: FiUsers },
  { to: '/contact', key: 'contact', icon: FiPhoneCall },
];

const LocaleFlag = ({ locale }) => {
  const meta = getLocaleFlagMeta(locale);
  if (meta.flagClass === 'is-kurdistan') {
    return (
      <span className="locale-flag locale-flag--kurdistan" aria-hidden="true" title={meta.label}>
        <span />
        <span />
        <span />
        <i />
      </span>
    );
  }
  return <span className="locale-flag" aria-hidden="true">{meta.flag}</span>;
};

const LanguageSwitcher = ({ className = '', layout = 'row' }) => {
  const { t } = useTranslation();
  const { locale, locales, changeLocale } = useLocale();
  const enabled = locales.filter((item) => item.enabled !== false);

  return (
    <div
      className={`language-switcher language-switcher--${layout} ${className}`.trim()}
      role="group"
      aria-label={t('language.change')}
    >
      {enabled.map((item) => {
        const meta = getLocaleFlagMeta(item);
        const active = item.code === locale.code;
        return (
          <button
            key={item.code}
            type="button"
            className={`language-switcher__option ${active ? 'is-active' : ''}`}
            onClick={() => changeLocale(item.code)}
            aria-pressed={active}
            aria-label={meta.label}
            title={meta.label}
          >
            <LocaleFlag locale={item} />
            <span>{meta.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [avatarFailed, setAvatarFailed] = useState(false);
  const lastScrollY = useRef(0);
  const menuToggleRef = useRef(null);
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const siteSettings = settings.general;
  const logoUrl = resolveSettingMediaUrl(siteSettings.logoUrl) || '/logo/logo.webp';

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY.current;

      setIsNavbarVisible(currentScrollY < 16 || !scrollingDown || isMenuOpen);
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [user?.avatar]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuToggleRef.current?.focus();
      }
    };

    document.body.classList.add('navbar-menu-open');
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.classList.remove('navbar-menu-open');
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const desktopViewport = window.matchMedia('(min-width: 921px)');
    const handleViewportChange = (event) => {
      if (event.matches) setIsMenuOpen(false);
    };

    desktopViewport.addEventListener?.('change', handleViewportChange);
    return () => desktopViewport.removeEventListener?.('change', handleViewportChange);
  }, []);

  const isActive = (path) => {
    const localizedPath = localePath(path);
    return path === '/'
      ? location.pathname === localizedPath
      : location.pathname.startsWith(localizedPath);
  };

  const openMenu = () => {
    setIsNavbarVisible(true);
    setIsMenuOpen(true);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;

    navigate(`${localePath('/products')}?search=${encodeURIComponent(query)}`);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate(localePath('/'));
    setIsMenuOpen(false);
  };

  const handleLogoError = (event) => {
    if (event.currentTarget.src !== `${window.location.origin}/logo/logo.webp`) {
      event.currentTarget.src = '/logo/logo.webp';
    } else {
      event.currentTarget.style.display = 'none';
    }
  };

  const mobileMenu = isMenuOpen && typeof document !== 'undefined'
    ? createPortal(
      <div className="mobile-nav-layer">
        <button
          type="button"
          className="mobile-nav-layer__backdrop"
          onClick={closeMenu}
          aria-label={t('nav.closeMenu')}
        />

        <aside
          id="mobile-navigation-drawer"
          className="mobile-nav-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-navigation-title"
        >
          <div className="mobile-nav-drawer__header">
            <Link to={localePath('/')} className="mobile-nav-drawer__brand" aria-label={t('nav.home')}>
              <span className="mobile-nav-drawer__brand-logo">
                <img
                  src={logoUrl}
                  alt=""
                  aria-hidden="true"
                  onError={handleLogoError}
                />
              </span>
              <span className="mobile-nav-drawer__brand-copy">
                <strong id="mobile-navigation-title">{siteSettings.siteTitle || t('home.title')}</strong>
                <small>{siteSettings.tagline || t('footer.slogan')}</small>
              </span>
            </Link>

            <button
              type="button"
              className="mobile-nav-drawer__close ui-close-button"
              onClick={closeMenu}
              aria-label={t('nav.closeMenu')}
              autoFocus
            >
              <FiX aria-hidden="true" />
            </button>
          </div>

          <div className="mobile-nav-drawer__scroll">
            <form className="mobile-nav-search" onSubmit={handleSearch}>
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                placeholder={t('nav.searchProducts')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                aria-label={t('nav.searchProducts')}
              />
              <button type="submit" aria-label={t('nav.search')}>
                <FiArrowLeft aria-hidden="true" />
              </button>
            </form>

            <div className="mobile-nav-language">
              <span>{t('language.label')}</span>
              <LanguageSwitcher layout="wrap" />
            </div>

            <div className="mobile-nav-account">
              {isAuthenticated ? (
                <>
                  <Link to={localePath('/user-panel')} className="mobile-nav-account__profile">
                    <span className="mobile-nav-account__avatar">
                      {user?.avatar && !avatarFailed ? (
                        <img
                          src={resolveSettingMediaUrl(user.avatar)}
                          alt={user?.name || t('nav.profileImage')}
                          onError={() => setAvatarFailed(true)}
                        />
                      ) : (user?.name || t('nav.user')).trim().charAt(0)}
                    </span>
                    <span>
                      <small>{t('nav.welcome')}</small>
                      <strong>{user?.name || t('nav.haryadUser')}</strong>
                    </span>
                    <FiArrowLeft aria-hidden="true" />
                  </Link>

                  <div className="mobile-nav-account__actions">
                    {user?.role === 'admin' && (
                      <Link to="/admin-panel">
                        <FiGrid aria-hidden="true" />
                        {t('nav.admin')}
                      </Link>
                    )}
                    <button type="button" onClick={handleLogout}>
                      <FiLogOut aria-hidden="true" />
                      {t('nav.logout')}
                    </button>
                  </div>
                </>
              ) : (
                <div className="mobile-nav-account__guest">
                  <Link to={localePath('/login')}>
                    <FiLogIn aria-hidden="true" />
                    {t('nav.login')}
                  </Link>
                  {settings.status.registrationEnabled && (
                    <Link to={localePath('/register')}>
                      <FiUserPlus aria-hidden="true" />
                      {t('nav.register')}
                    </Link>
                  )}
                </div>
              )}
            </div>

            <nav className="mobile-nav-drawer__navigation" aria-label={t('nav.mobileMenu')}>
              <ul className="mobile-nav-links">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);

                  return (
                    <li key={item.to}>
                      <Link
                        to={localePath(item.to)}
                        className={active ? 'is-active' : ''}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="mobile-nav-links__icon">
                          <Icon aria-hidden="true" />
                        </span>
                        <span className="mobile-nav-links__copy">
                          <strong>{t(`nav.${item.key}`)}</strong>
                          <small>{t(`navDescription.${item.key}`)}</small>
                        </span>
                        <FiArrowLeft className="mobile-nav-links__arrow" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="mobile-nav-drawer__footer">
            <span />
            <p>{t('nav.digitalGrowth')}</p>
          </div>
        </aside>
      </div>,
      document.body
    )
    : null;

  return (
    <>
      <header className={`site-navbar ${isNavbarVisible ? 'is-visible' : 'is-hidden'}`}>
        <div className="site-navbar__container">
          <Link to={localePath('/')} className="site-navbar__brand" aria-label={t('nav.home')}>
            <img
              src={logoUrl}
              alt={siteSettings.siteTitle || t('home.title')}
              className="site-navbar__logo"
              onError={handleLogoError}
            />
          </Link>

          <nav className="site-navbar__desktop-nav" aria-label={t('nav.mainMenu')}>
            <ul className="site-navbar__desktop-links">
              {navigationItems.map((item) => {
                const active = isActive(item.to);
                return (
                  <li key={item.to}>
                    <Link
                      to={localePath(item.to)}
                      className={active ? 'is-active' : ''}
                      aria-current={active ? 'page' : undefined}
                    >
                      {t(`nav.${item.key}`)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="site-navbar__actions">
            <LanguageSwitcher className="site-navbar__language" />
            <form className="site-navbar__search" onSubmit={handleSearch}>
              <input
                type="search"
                placeholder={t('nav.search')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                aria-label={t('nav.searchProducts')}
              />
              <button type="submit" aria-label={t('nav.search')}>
                <FiSearch aria-hidden="true" />
              </button>
            </form>

            {isAuthenticated ? (
              <div className="site-navbar__user">
                <Link to={localePath('/user-panel')} className="site-navbar__account" title={t('nav.account')}>
                  <span className="site-navbar__avatar">
                    {user?.avatar && !avatarFailed ? (
                      <img
                        src={resolveSettingMediaUrl(user.avatar)}
                        alt={user?.name || t('nav.profileImage')}
                        onError={() => setAvatarFailed(true)}
                      />
                    ) : (user?.name || t('nav.user')).trim().charAt(0)}
                  </span>
                  <span className="site-navbar__account-copy">
                    <small>{t('nav.account')}</small>
                    <strong>{user?.name?.split(' ')[0] || t('nav.myPanel')}</strong>
                  </span>
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin-panel" className="site-navbar__icon-button" title={t('nav.admin')}>
                    <FiGrid aria-hidden="true" />
                  </Link>
                )}
                <button
                  type="button"
                  className="site-navbar__icon-button is-logout"
                  onClick={handleLogout}
                  title={t('nav.logout')}
                >
                  <FiLogOut aria-hidden="true" />
                </button>
              </div>
            ) : (
              <div className="site-navbar__auth">
                <Link to={localePath('/login')} className="site-navbar__login">{t('nav.login')}</Link>
                {settings.status.registrationEnabled && (
                  <Link to={localePath('/register')} className="site-navbar__register">{t('nav.register')}</Link>
                )}
              </div>
            )}

            <button
              ref={menuToggleRef}
              type="button"
              className="site-navbar__toggle"
              onClick={openMenu}
              aria-label={t('nav.openMenu')}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation-drawer"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {mobileMenu}
    </>
  );
};

export default Navbar;
